import React, { useState, useEffect, useRef } from 'react'
import './Career.css'

import {
    LazyLoadImage,
    LazyLoadComponent,
} from 'react-lazy-load-image-component';

// Assets
// Background video
const BannerBg = require('./videos/opportunities-page-video.mp4');
const BannerBg_LazyLoadImg = require('./img/opportinities-lazyload.png');

// Images
import careerBg1 from './img/career-bg1.png'
import cloud from './img/cloud-add2.png'

// Department pictures
import compsci from './img/compsci.jpg'
import electrical from './img/electrical.jpg'
import aero_mech from './img/aero-mech.jpg'
import gis from './img/gis.jpg'
import marketing from './img/marketing.jpg'

// Icons
import { LiaLaptopSolid } from "react-icons/lia";
import { LuCircuitBoard } from "react-icons/lu";
import { LiaToolsSolid } from "react-icons/lia";
import { FaGlobeAmericas, FaLaptopHouse, FaMapMarkerAlt, FaChartLine } from "react-icons/fa";
import { BsFillBriefcaseFill } from "react-icons/bs";
import { FaBuildingUser, FaSeedling } from "react-icons/fa6";
import { FiWifi } from "react-icons/fi";
import { MdTimelapse, MdOutlineCalendarMonth } from "react-icons/md";
import { AiOutlineClockCircle } from "react-icons/ai";
import { IoMdSchool, IoIosLeaf } from "react-icons/io";
import { IoSunny } from "react-icons/io5";

type JobDescription = {
    id: number;
    position: string;
    department: string;
    date_posted: string; // ISO format, typically from Django (e.g., "2025-05-01T14:00:00Z")
    type: "In-person" | "Hybrid" | "Remote";
    city_state_or_online: string;
    commitment?: "Full-time" | "Part-time" | "Internship";
    semester?: string;
    summary: string;
    description: string;
    requirements?: string | null;
    responsibilities?: string | null;
};


function timeSince(date: Date): string {
    // Get NY timezone strings for both dates at midnight
    const todayNYMidnight = new Date(
        new Date().toLocaleString("en-US", { timeZone: "America/New_York" })
    );
    todayNYMidnight.setHours(0, 0, 0, 0); // Midnight today

    const postNYMidnight = new Date(
        date.toLocaleString("en-US", { timeZone: "America/New_York" })
    );
    postNYMidnight.setHours(0, 0, 0, 0); // Midnight of post date

    const diffTime = todayNYMidnight.getTime() - postNYMidnight.getTime();
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (days === 0) return "Today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
}

// Function for limiting the number of characters are shown on mobile
const truncateText = (text: string, maxLength: number) =>
    text.length > maxLength ? text.slice(0, maxLength) + '...' : text;




const Career = () => {

    // Section for the video and its lazyLoad functionality
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        specialty: '',
        file: null as File | null,
        job_id: 0,
        us_or_pr: '',
    });

    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const [job, setJob] = useState<JobDescription[]>([]);
    const [selectedJob, setSelectedJob] = useState<JobDescription | null>(null);
    const listingsRef = useRef<HTMLDivElement>(null);
    const [view, setView] = useState<"list" | "detail" | "success">("list");

    // Fake microseconds formatter
    const formatDateToMicroseconds = () => {
        const iso = new Date().toISOString(); // e.g., "2025-05-12T19:54:37.618Z"
        return iso.replace('Z', '000Z'); // pad with fake microseconds
    };



    const handleChange = (e: any) => {
        const { name, value, files } = e.target;

        if (name === 'file') {
            setFormData({ ...formData, [name]: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin",
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {

            if (selectedJob) {
                data.append('job_id', String(selectedJob.id));
            } else {
                data.append('job_id', String(0));
            }

            // Append formatted date_applied
            const dateApplied = formatDateToMicroseconds();
            data.append('date_applied', dateApplied);

            const response = await fetch('/api/submit-application/', {
                method: 'POST',
                credentials: "same-origin",
                headers: { 'X-CSRFToken': csrfToken },
                body: data,
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            setView("success");
            setSelectedJob(null);
            setError('');

            setTimeout(() => {
                setView("list");
                setFormData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    specialty: '',
                    phone: '',
                    file: null,
                    job_id: 0,
                    us_or_pr: '',
                });
            }, 6000);

        } catch (err) {
            console.error('Error submitting the form:', err);
            setError('Something went wrong. Please try again.');
        }
    };



    // Handle Drag and Drop Events
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            setFormData({ ...formData, file: file });
        }
    };

    const dropZone = document.querySelector('.resume-drag-and-drop');

    if (dropZone) {
        dropZone.addEventListener('dragover', (e) => {
            e.preventDefault(); // important! Allows drop
            dropZone.classList.add('dragover');
        });

        dropZone.addEventListener('dragleave', () => {
            dropZone.classList.remove('dragover');
        });

        dropZone.addEventListener('drop', () => {
            dropZone.classList.remove('dragover');
        });
    }

    function determinePicture(department: string) {
        switch (department) {
            case "Computer Science":
                return <img alt="job-image" src={compsci} />
            case "Electrical Engineering":
                return <img alt="job-image" src={electrical} />
            case "Aerospace/Mechanical Engineering":
                return <img alt="job-image" src={aero_mech} />
            case "Geographic Information Systems (GIS)":
                return <img alt="job-image" src={gis} />
            case "Marketing":
                return <img alt="job-image" src={marketing} />
            default:
                return <img alt="job-image" src={careerBg1} />
        }
    }

    function determineColor(position: string) {
        switch (position) {
            case "Computer Science":
                return " #28aae1"
            case "Electrical Engineering":
                return " #3D708F"
            case "Aerospace/Mechanical Engineering":
                return " #045D94"
            case "Geographic Information Systems (GIS)":
                return " #45A0D7"
            case "Marketing":
                return " #46A6CF"
            default:
                return " #778B93"
        }
    }

    function determineIcon(position: string) {
        switch (position) {
            case "Computer Science":
                return <LiaLaptopSolid className='job-logo' />
            case "Electrical Engineering":
                return <LuCircuitBoard className='job-logo' />
            case "Aerospace/Mechanical Engineering":
                return <LiaToolsSolid className='job-logo' />
            case "Geographic Information Systems (GIS)":
                return <FaGlobeAmericas className='job-logo' />
            case "Marketing":
                return <FaChartLine className='job-logo' />
            default:
                return <BsFillBriefcaseFill className='job-logo' />
        }
    }

    function determineType(type: string) {
        switch (type) {
            case "In-person":
                return <FaBuildingUser className='job-type' />
            case "Hybrid":
                return <FaLaptopHouse className='job-type' />
            case "Remote":
                return <FiWifi className='job-type' />
        }
    }

    function determineCommitment(type: string) {
        switch (type) {
            case "Full-time":
                return <AiOutlineClockCircle className='job-type' />
            case "Part-time":
                return <MdTimelapse className='job-type' />
            case "Internship":
                return <IoMdSchool className='job-type' />
        }
    }

    function determineSemester(type: string) {

        if (type.startsWith("Fall")) {
            return <IoIosLeaf className='job-type' />
        }

        else if (type.startsWith("Spring")) {
            return <FaSeedling className='job-type' />
        }

        else if (type.startsWith("Summer")) {
            return <IoSunny className='job-type' />
        }
    }

    // Fetching Jobs
    const fetchMainPost = async () => {
        try {
            const response = await fetch('/api/job_posting', {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data: JobDescription[] = await response.json();
            setJob(data); // Assign the fetched posts to state
        } catch (err) {
            // Type assertion to 'Error' to access the message property
            if (err instanceof Error) {
                setError(err.message); // Handle error
            } else {
                setError('An unknown error occurred'); // Fallback for unknown errors
            }
        }
    };

    useEffect(() => {
        fetchMainPost();
    }, []); // Empty dependency array to run once on mount



    return (
        <div className="home-container">
            <div className="home-hero-section">
                {!isVideoLoaded && (
                    <LazyLoadComponent
                        placeholder={<img src={BannerBg_LazyLoadImg} alt="" />}
                    >
                        <LazyLoadImage
                            src={BannerBg_LazyLoadImg}
                            alt="="
                            className="hero-image"
                        />
                    </LazyLoadComponent>
                )}

                <video
                    className={`hero-image ${isVideoLoaded ? '' : 'hidden'}`}
                    src={BannerBg}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onLoadedData={() => setIsVideoLoaded(true)}
                />

                <div className="dark-overlay"></div> {/* ← Overlay */}
                <div className="home-info-wrapper">
                    <h1 className='opp-for-mobile'><strong>Careers</strong></h1>
                    <h2>
                        Think you could be a valuable member of our team? Reach out and send an application.
                    </h2>
                </div>
            </div>

            <div className="get-in-touch-wrapper" ref={listingsRef}>

                <h1 style={{ color: '#28AAE1', fontWeight: '700' }}>
                    {job.length > 0 ? "Opportunities" : "Application"}
                </h1>

                {view === "success" && (
                    <h2 className="success-message" style={{ marginTop: '1rem' }}>Thank you for Applying!</h2>
                )}

                {/* Application without position */}
                {view === "list" && job.length === 0 && (
                    <form id="sib-form" className='sib-container--large sib-container--vertical no-pos' onSubmit={handleSubmit}>

                        {/* First Name */}
                        <div>
                            <div className="sib-form-block">
                                <input
                                    className="sibs-input"
                                    maxLength={200}
                                    type="text"
                                    name="first_name"
                                    placeholder="First Name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Last Name */}
                        <div>
                            <div className="sib-input sib-form-block">
                                <input
                                    className="sibs-input"
                                    maxLength={200}
                                    type="text"
                                    name="last_name"
                                    placeholder="Last Name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <div className="sib-input sib-form-block">
                                <input
                                    className="sibs-input"
                                    type="email"
                                    name="email"
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Phone */}
                        <div>
                            <div className="sib-input sib-form-block">
                                <input
                                    className="sibs-input"
                                    type="text"
                                    name="phone"
                                    placeholder="Phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Specialty */}
                        <div>
                            <div className="sib-input sib-form-block">
                                <label>
                                    Specialty<span style={{ color: 'red' }}>*</span>
                                </label>
                                <select
                                    className="sibs-input sibs-select-input"
                                    name="specialty"
                                    value={formData.specialty}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="" disabled>Select One</option>
                                    <option value="Computer Science">Computer Science</option>
                                    <option value="Electrical Engineering">Electrical Engineering</option>
                                    <option value="Aerospace/Mechanical Engineering">Aerospace/Mechanical Engineering</option>
                                    <option value="Geographic Information System">Geographic Information System</option>
                                    <option value="Marketing">Marketing</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        {/* US Citizen or Permanent Resident */}
                        <div>
                            <div className="sib-input sib-form-block">
                                <label>
                                    Are you a U.S. Citizen or Permanent Resident?
                                    <span style={{ color: 'red' }}>*</span>
                                </label>

                                <select
                                    className="sibs-input sibs-select-input"
                                    name="us_or_pr"
                                    value={formData.us_or_pr}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="" disabled>Select One</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>
                        </div>

                        {/* Resume Upload */}
                        <div>
                            <div className="sib-input sib-form-block">
                                <label>
                                    Resume<span style={{ color: 'red' }}>*</span>
                                </label>

                                <div className='resume-wrapper'>
                                    {formData.file ? (
                                        <div className="uploaded-file-name">
                                            <h2>Selected File:</h2>
                                            <p>{formData.file.name}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className='resume-image-plus-types'>
                                                <img src={cloud} alt='cloud-img' className='cloud-image' />
                                                <div className='resume-types'>
                                                    <h1>Upload files</h1>
                                                    <p>DOC, DOCX, PDF, or PNG</p>
                                                </div>
                                            </div>

                                            <div
                                                className={`resume-drag-and-drop ${dragActive ? 'drag-active' : ''}`}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}
                                            >
                                                <img src={cloud} alt='cloud-img2' className='cloud-image2' />
                                                <h1>Choose a file or drag & drop it here</h1>
                                                <p>DOC, DOCX, PDF, or PNG</p>
                                            </div>
                                        </>
                                    )}

                                    {/* Hidden input */}
                                    <input
                                        id="resume-upload"
                                        className="career-resume-input"
                                        type="file"
                                        name="file"
                                        accept=".pdf,.doc,.docx,.png"
                                        onChange={handleChange}
                                        required
                                    />

                                    {/* Custom button */}
                                    <div className='career-resume-button-wrapper'>
                                        <label htmlFor="resume-upload" className="career-resume-button">
                                            Browse Files
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div>
                            <button
                                className="sibs-submit-button"
                                type="submit"
                            >
                                Submit
                            </button>
                        </div>

                        {error && <p className="error-message">{error}</p>}
                    </form>

                )}

                {/* Application with positions */}
                {view === "list" && (
                    <div className='jobs-card-stack'>

                        {job.map((jobItem) => (
                            <div
                                key={jobItem.id}
                                className='job-card'
                                onClick={() => {
                                    setSelectedJob(jobItem);
                                    setView("detail");
                                }}
                            >
                                {determinePicture(jobItem.department)}
                                <div className='job-card-info'>
                                    <h1>{truncateText(jobItem.position, 50)}</h1>
                                    <p>{truncateText(jobItem.summary, 200)}</p>

                                    <div
                                        className='job-card-info-banner'
                                        style={{ backgroundColor: determineColor(jobItem.department) }}
                                    >
                                        <p>
                                            {new Date(jobItem.date_posted).toLocaleString('en-US', {
                                                timeZone: 'America/New_York',
                                                dateStyle: 'long'
                                            })}
                                        </p>
                                        {determineIcon(jobItem.department)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {view === "detail" && selectedJob && (
                    <div className='apply-container'>
                        <div className='job-description-wrapper'>
                            <h1 className='my-job-h1' style={{ color: '#28AAE1', fontWeight: '700' }}>
                                {selectedJob.position}
                            </h1>

                            <div className='job-description-misc-wrapper'>
                                <div className='job-description-misc'>
                                    <p>{determineType(selectedJob.type)} {selectedJob.type}</p>
                                    <p><FaMapMarkerAlt className='job-type' /> {selectedJob.city_state_or_online}</p>

                                    {selectedJob.commitment && (
                                        <p>{determineCommitment(selectedJob.commitment)} {selectedJob.commitment}</p>
                                    )}
                                </div>

                                <div className='job-description-misc'>
                                    <p><MdOutlineCalendarMonth className='job-type' /> Posted {timeSince(new Date(selectedJob.date_posted))}</p>

                                    {selectedJob.semester && (
                                        <p>{determineSemester(selectedJob.semester)} {selectedJob.semester}</p>
                                    )}
                                </div>
                            </div>

                            <div className='job-description-info'>
                                <h2 style={{ color: '#28AAE1' }}>{selectedJob.department}</h2>
                                <h3 style={{ color: '#28AAE1' }}>Description</h3>
                                <p>{selectedJob.description}</p>
                                {selectedJob.requirements && selectedJob.requirements !== "Nothing" && (
                                    <>
                                        <h3 style={{ color: '#28AAE1' }}>Requirements</h3>
                                        {selectedJob.requirements.split('\n').map((line, index) => (
                                            <p style={{ marginBottom: '0.3rem' }} key={index}>{line}</p>
                                        ))}
                                    </>
                                )}

                                <div style={{ marginBottom: '2rem' }}></div>

                                {selectedJob.responsibilities && selectedJob.responsibilities !== "Nothing" && (
                                    <>
                                        <h3 style={{ color: '#28AAE1' }}>Responsibilities</h3>
                                        {selectedJob.responsibilities.split('\n').map((line, index) => (
                                            <p style={{ marginBottom: '0.3rem' }} key={index}>{line}</p>
                                        ))}
                                    </>
                                )}
                            </div>

                            <button
                                onClick={() => {
                                    setSelectedJob(null);
                                    setView("list");
                                    setTimeout(() => {
                                        if (listingsRef.current) {
                                            const yOffset = -55; // Offset in pixels
                                            const y = listingsRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                            window.scrollTo({ top: y, behavior: 'smooth' });
                                        }
                                    }, 0);
                                }}
                                className="the-button2"
                                style={{ marginTop: '3rem' }}
                            >
                                <span>← Back to Listings</span>
                            </button>


                        </div>

                        <div id="sib-container" className="sib-container--large sib-container--vertical career-change">
                            {submitted ? (
                                <h2 className="success-message">Thank you for Applying!</h2>
                            ) : (
                                <form id="sib-form" onSubmit={handleSubmit}>
                                    <h1 className='my-job-h1' style={{ color: '#28AAE1', fontWeight: '700' }}>Application</h1>
                                    {/* First Name */}
                                    <div>
                                        <div className="sib-form-block">
                                            <input
                                                className="sibs-input for-appli"
                                                maxLength={200}
                                                type="text"
                                                name="first_name"
                                                placeholder="First Name"
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Last Name */}
                                    <div>
                                        <div className="sib-input sib-form-block">
                                            <input
                                                className="sibs-input for-appli"
                                                maxLength={200}
                                                type="text"
                                                name="last_name"
                                                placeholder="Last Name"
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <div className="sib-input sib-form-block">
                                            <input
                                                className="sibs-input for-appli"
                                                type="email"
                                                name="email"
                                                placeholder="Email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <div className="sib-input sib-form-block">
                                            <input
                                                className="sibs-input for-appli"
                                                type="text"
                                                name="phone"
                                                placeholder="Phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Specialty */}
                                    <div>
                                        <div className="sib-input sib-form-block">
                                            <label>
                                                Specialty<span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <select
                                                className="sibs-input sibs-select-input for-appli"
                                                name="specialty"
                                                value={formData.specialty}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="" disabled>Select One</option>
                                                <option value="Computer Science">Computer Science</option>
                                                <option value="Electrical Engineering">Electrical Engineering</option>
                                                <option value="Aerospace/Mechanical Engineering">Aerospace/Mechanical Engineering</option>
                                                <option value="Geographic Information System">Geographic Information System</option>
                                                <option value="Marketing">Marketing</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* US Citizen or Permanent Resident */}
                                    <div>
                                        <div className="sib-input sib-form-block">
                                            <label>
                                                Are you a U.S. Citizen or Permanent Resident?
                                                <span style={{ color: 'red' }}>*</span>
                                            </label>

                                            <select
                                                className="sibs-input sibs-select-input"
                                                name="us_or_pr"
                                                value={formData.us_or_pr}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="" disabled>Select One</option>
                                                <option value="true">Yes</option>
                                                <option value="false">No</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Resume Upload */}
                                    <div>
                                        <div className="sib-input sib-form-block">
                                            <label>
                                                Resume<span style={{ color: 'red' }}>*</span>
                                            </label>

                                            <div className='resume-wrapper for-appli'>
                                                {formData.file ? (
                                                    <div className="uploaded-file-name">
                                                        <h2>Selected File:</h2>
                                                        <p>{formData.file.name}</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className='resume-image-plus-types'>
                                                            <img src={cloud} alt='cloud-img' className='cloud-image' />
                                                            <div className='resume-types'>
                                                                <h1>Upload files</h1>
                                                                <p>DOC, DOCX, PDF, or PNG</p>
                                                            </div>
                                                        </div>

                                                        <div
                                                            className={`resume-drag-and-drop ${dragActive ? 'drag-active' : ''}`}
                                                            onDragOver={handleDragOver}
                                                            onDragLeave={handleDragLeave}
                                                            onDrop={handleDrop}
                                                        >
                                                            <img src={cloud} alt='cloud-img2' className='cloud-image2' />
                                                            <h1>Choose a file or drag & drop it here</h1>
                                                            <p>DOC, DOCX, PDF, or PNG</p>
                                                        </div>
                                                    </>
                                                )}

                                                {/* Hidden input */}
                                                <input
                                                    id="resume-upload"
                                                    className="career-resume-input"
                                                    type="file"
                                                    name="file"
                                                    accept=".pdf,.doc,.docx,.png"
                                                    onChange={handleChange}
                                                    required
                                                />

                                                {/* Custom button */}
                                                <div className='career-resume-button-wrapper'>
                                                    <label htmlFor="resume-upload" className="career-resume-button">
                                                        Browse Files
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div>
                                        <button
                                            className="sibs-submit-button"
                                            type="submit"
                                        >
                                            Submit
                                        </button>
                                    </div>

                                    {error && <p className="error-message">{error}</p>}
                                </form>
                            )}
                        </div>
                    </div>
                )}


            </div>
        </div>
    )
}

export default Career
